// src/components/register/RegistrationWizard/Steps/EducationalDetailsStep.js
import React, { useState, useCallback } from "react";
import { useRegistration } from "../../../../context/RegistrationContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import OpguruLogo from "../../../../assets/opgurulogo.png";
import { TailSpin } from "react-loader-spinner";
import axios from "../../../../api/axios";
import { useLoader } from "../../../../context/LoaderContext";

// Import Educational Form Components
import Below8thForm from "./EducationalForms/Below8thForm";
import NinthTenthForm from "./EducationalForms/NinthTenthForm";
import ElevenTwelfthForm from "./EducationalForms/ElevenTwelfthForm";
import BachelorsForm from "./EducationalForms/BachelorsForm";
import MastersForm from "./EducationalForms/MastersForm";
import PhdForm from "./EducationalForms/PhdForm";
// Import the necessary option arrays
import SchoolBoard from "../../../options/schoolBoard";
import CurrentClassBelow8th from "../../../options/CurrentClass8th";
import CurrentClass910 from "../../../options/CurrentClass910";
import CurrentClass1112 from "../../../options/CurrentClass1011";
import Streams from "../../../options/Streams";
import YearOfStudy from "../../../userForms/FormsData/Year";
import CollegeList from "../../../userForms/FormsData/Colleges";
import DegreeList from "../../../options/MasterDegree";
import Specializations from "../../../options/phdDegree";
import FutureDegree from "../../../options/FutureDegree";
import FutureFieldsOptions from "../../../userForms/FormsData/FutureFields";
import OlympiadsOptions from "../../../options/Olympiads";
import OpportunitiesOptions from "../../../options/OpportunitiesTypes8th";
import SubjectsOptions from "../../../options/InterestedSubjects8th";
import OlympiadTypesOptions from "../../../options/OlympiadTypes11_12th";
import Gender from "../../../options/Gender";

const EducationalDetailsStep = () => {
  const {
    educationLevel,
    educationalDetails,
    setEducationalDetails,
    prevStep,
    generatedUserId,
    userType,
    firstname,
    lastname,
    gender,
    country,
    city,
    homestate,
    pin,
    setField,
    resetForm,
    access_token,
  } = useRegistration();

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setIsLoading } = useLoader();

  // const REGISTER_URL = "/api/register";
  const SAVE_LOGIN_ROUTE = "/api/saveLoginRoute";
  const SAVE_PERSONALDETAILS_ROUTE = "/api/savePersonalDetails";
  const SAVE_CATEGORY = "/api/saveCategory";
  const SAVE_OPPS_ROUTE = "/api/saveOpps";

  // Handle educational details change from child forms
  const handleEducationalDetailsChange = useCallback(
    (details) => {
      setEducationalDetails(details);
    },
    [setEducationalDetails]
  );

  // Helper functions for each education level
  const prepareBelowEighthPrompt = (formData) => {
    // Convert values to labels where needed
    const currentClassLabel = getCurrentClassLabel(
      formData.currentclass,
      "Below8th"
    );
    const schoolBoardLabel = getSchoolBoardLabel(formData.schoolBoard);

    // Format subjects and olympiads
    const subjects = parseJsonStringToLabels(
      formData.subjects,
      SubjectsOptions
    );
    const olympiads = parseJsonStringToLabels(
      formData.olympiads,
      OlympiadsOptions
    );
    const opportunities = parseJsonStringToLabels(
      formData.opportunities,
      OpportunitiesOptions
    );

    return `
  EDUCATIONAL BACKGROUND:
  - Current class: ${currentClassLabel}
  - School: ${formData.schoolname || "Unspecified"}
  - School board: ${schoolBoardLabel}
  
  INTERESTS AND PREFERENCES:
  - Preferred subjects: ${subjects.join(", ") || "None specified"}
  - Olympiads participated in: ${olympiads.join(", ") || "None"}
  - Types of opportunities interested in: ${
    opportunities.join(", ") || "None specified"
  }
  `;
  };

  const prepareNinthTenthPrompt = (formData) => {
    // Similar pattern to Below8th with 9-10th specific fields
    const currentClassLabel = getCurrentClassLabel(
      formData.currentclass,
      "9-10th"
    );
    const schoolBoardLabel = getSchoolBoardLabel(formData.schoolBoard);
    const futureStreamLabel = getStreamLabel(formData.futurestream);

    const subjects = parseJsonStringToLabels(
      formData.subjects,
      SubjectsOptions
    );
    const olympiads = parseJsonStringToLabels(
      formData.olympiads,
      OlympiadsOptions
    );
    const opportunities = parseJsonStringToLabels(
      formData.opportunities,
      OpportunitiesOptions
    );

    return `
  EDUCATIONAL BACKGROUND:
  - Current class: ${currentClassLabel}
  - School: ${formData.schoolname || "Unspecified"}
  - School board: ${schoolBoardLabel}
  
  INTERESTS AND PREFERENCES:
  - Preferred future stream: ${futureStreamLabel}
  - Preferred subjects: ${subjects.join(", ") || "None specified"}
  - Olympiads participated in: ${olympiads.join(", ") || "None"}
  - Types of opportunities interested in: ${
    opportunities.join(", ") || "None specified"
  }
  `;
  };

  const prepareElevenTwelfthPrompt = (formData) => {
    // Format for 11-12th students
    const currentClassLabel = getCurrentClassLabel(
      formData.currentclass,
      "11-12th"
    );
    const schoolBoardLabel = getSchoolBoardLabel(formData.schoolBoard);
    const tenthBoardLabel = getSchoolBoardLabel(formData.class10thboard);
    const currentStreamLabel = getStreamLabel(formData.currentstream);
    const futureDegreeLabel = getFutureDegreeLabel(formData.futuredegree);

    const olympiadsParticipated = parseJsonStringToLabels(
      formData.olympiadparticipated,
      OlympiadsOptions
    );
    const olympiadsInterested = parseJsonStringToLabels(
      formData.olympiadinterested,
      OlympiadsOptions
    );
    const futureOlympiadTypes = parseJsonStringToLabels(
      formData.futureolympiadtypes,
      OlympiadTypesOptions
    );

    // Format exams
    const exams = parseExams(formData.exams);

    return `
  EDUCATIONAL BACKGROUND:
  - Current class: ${currentClassLabel}
  - Current stream: ${currentStreamLabel}
  - School: ${formData.schoolname || "Unspecified"}
  - School board: ${schoolBoardLabel}
  - 10th percentage: ${formData.class10percent || "N/A"}% 
  - 10th board: ${tenthBoardLabel}
  - 10th school: ${formData.tenthschoolname || "Unspecified"}
  - Best of 5 marks: ${formData.BestOfFive || "N/A"}
  - Aggregate percentage: ${formData.aggregatepercent || "N/A"}
  
  EXAMINATION HISTORY:
  - Exams taken: ${exams || "None mentioned"}
  
  INTERESTS AND PREFERENCES:
  - Olympiads participated in: ${olympiadsParticipated.join(", ") || "None"}
  - Olympiads interested in: ${olympiadsInterested.join(", ") || "None"}
  - Olympiad types interested in: ${
    futureOlympiadTypes.join(", ") || "None specified"
  }
  - Preferred future degree: ${futureDegreeLabel}
  - Budget range: ${formData.budget || "Not specified"}
  `;
  };

  const prepareBachelorsPrompt = (formData) => {
    // Format for Bachelor's students
    const collegeLabel = getCollegeLabel(formData.college);
    const yearLabel = getYearLabel(formData.year);
    const tenthBoardLabel = getSchoolBoardLabel(formData.tenthBoard);
    const twelthBoardLabel = getSchoolBoardLabel(formData.twelthBoard);

    const futureFields = formData.futurefields
      ? formData.futurefields.map((field) => field.label).join(", ")
      : "None specified";
    const olympiads = formData.olympiadparticipated
      ? formData.olympiadparticipated
          .map((olympiad) => olympiad.label)
          .join(", ")
      : "None";

    // Format complex structures
    const exams = parseExams(formData.exams);
    const regions = parseRegions(formData.interestedregions);
    const internship = parseInternship(formData.intern);

    return `
  EDUCATIONAL BACKGROUND:
  - Current institution: ${collegeLabel}
  - Current year of study: ${yearLabel}
  - 10th percentage: ${formData.tenthPercentage || "N/A"}% 
  - 10th board: ${tenthBoardLabel}
  - 10th school: ${formData.tenthschoolname || "Unspecified"}
  - 12th percentage: ${formData.twelthPercentage || "N/A"}% 
  - 12th board: ${twelthBoardLabel}
  - 12th school: ${formData.twelthschoolname || "Unspecified"}
  
  EXAMINATION HISTORY:
  - Exams taken: ${exams || "None mentioned"}
  
  INTERESTS AND PREFERENCES:
  - Fields of interest: ${futureFields}
  - Olympiads participated in: ${olympiads}
  - Regions interested in: ${regions || "No specific regions mentioned"}
  
  `;
  };

  const prepareMastersPrompt = (formData) => {
    // Format for Master's students
    const collegeLabel = getCollegeLabel(formData.collegename);
    const yearLabel = getYearLabel(formData.year);
    const tenthBoardLabel = getSchoolBoardLabel(formData.tenthBoard);
    const twelthBoardLabel = getSchoolBoardLabel(formData.twelthBoard);
    const currentDegreeLabel = getDegreeLabel(formData.currentdegree);
    const futureDegreeLabel = getDegreeLabel(formData.futuredegree);

    const futureOpportunityTypes = parseJsonStringToLabels(
      formData.futureOpportunityTypes,
      FutureFieldsOptions
    );

    // Format complex structures
    const exams = parseExams(formData.exams);
    const regions = parseRegions(formData.interestedregions);

    return `
  EDUCATIONAL BACKGROUND:
  - Current institution: ${collegeLabel}
  - Current year of study: ${yearLabel}
  - Current degree: ${currentDegreeLabel}
  - GPA: ${formData.gpa || "Not specified"}
  - 10th percentage: ${formData.tenthPercentage || "N/A"}% 
  - 10th board: ${tenthBoardLabel}
  - 10th school: ${formData.tenthschoolname || "Unspecified"}
  - 12th percentage: ${formData.twelthPercentage || "N/A"}% 
  - 12th board: ${twelthBoardLabel}
  - 12th school: ${formData.twelthschoolname || "Unspecified"}
  
  EXAMINATION HISTORY:
  - Exams taken: ${exams || "None mentioned"}
  
  INTERESTS AND PREFERENCES:
  - Future degree interest: ${futureDegreeLabel}
  - Fields of interest: ${futureOpportunityTypes.join(", ") || "None specified"}
  - Regions interested in: ${regions || "No specific regions mentioned"}
  - Expected compensation: ${formData.compensation || "Not specified"}
  `;
  };

  const preparePhDPrompt = (formData) => {
    // Format for PhD students
    const collegeLabel = getCollegeLabel(formData.collegename);
    const yearLabel = getYearLabel(formData.year);
    const specializationLabel = getSpecializationLabel(formData.specialization);

    const futureOpportunityTypes = parseJsonStringToLabels(
      formData.futureOpportunityTypes,
      FutureFieldsOptions
    );

    // Format complex structures
    const exams = parseExams(formData.exams);
    const regions = parseRegions(formData.interestedregions);

    return `
  EDUCATIONAL BACKGROUND:
  - Current institution: ${collegeLabel}
  - Current year of study: ${yearLabel}
  - PhD specialization: ${specializationLabel}
  - GPA: ${formData.gpa || "Not specified"}
  
  EXAMINATION HISTORY:
  - Exams taken: ${exams || "None mentioned"}
  
  INTERESTS AND PREFERENCES:
  - Fields of interest: ${futureOpportunityTypes.join(", ") || "None specified"}
  - Regions interested in: ${regions || "No specific regions mentioned"}
  - Expected compensation: ${formData.compensation || "Not specified"}
  `;
  };

  // Utility functions to convert values to labels
  const getCurrentClassLabel = (value, level) => {
    if (!value) return "Unspecified";

    let options;
    if (level === "Below8th") {
      options = CurrentClassBelow8th;
    } else if (level === "9-10th") {
      options = CurrentClass910;
    } else {
      options = CurrentClass1112;
    }

    const option = options.find((item) => item.value === value);
    return option ? option.label : value;
  };

  const getSchoolBoardLabel = (value) => {
    if (!value) return "Unspecified";
    const option = SchoolBoard.find((item) => item.value === value);
    return option ? option.label : value;
  };

  const getStreamLabel = (value) => {
    if (!value) return "Unspecified";
    const option = Streams.find((item) => item.value === value);
    return option ? option.label : value;
  };

  const getYearLabel = (value) => {
    if (!value) return "Unspecified";
    const option = YearOfStudy.find((item) => item.value === value);
    return option ? option.label : value;
  };

  const getCollegeLabel = (value) => {
    if (!value) return "Unspecified";
    const option = CollegeList.find((item) => item.value === value);
    return option ? option.label : value;
  };
  const getGenderLabel = (value) => {
    if (!value) return "Unspecified";
    const option = Gender.find((item) => item.value === value);
    return option ? option.label : value;
  };

  const getDegreeLabel = (value) => {
    if (!value) return "Unspecified";
    const option = DegreeList.find((item) => item.value === value);
    return option ? option.label : value;
  };

  const getSpecializationLabel = (value) => {
    if (!value) return "Unspecified";
    const option = Specializations.find((item) => item.value === value);
    return option ? option.label : value;
  };

  const getFutureDegreeLabel = (value) => {
    if (!value) return "Unspecified";
    const option = FutureDegree.find((item) => item.value === value);
    return option ? option.label : value;
  };

  // Helper functions for complex structures
  const parseJsonStringToLabels = (jsonString, options) => {
    if (!jsonString) return [];

    try {
      const values = JSON.parse(jsonString);
      if (!Array.isArray(values)) return [];

      return values
        .map((value) => {
          const option = options.find((opt) => opt.value === value.toString());
          return option ? option.label : value;
        })
        .filter(Boolean);
    } catch (error) {
      console.error("Error parsing JSON string:", error);
      return [];
    }
  };

  const parseExams = (exams) => {
    if (!exams || exams.length === 0) return "None";

    return exams
      .map((exam) => {
        if (!exam.name) return null;
        let examText = exam.name;
        if (exam.percentage) examText += ` (Score: ${exam.percentage})`;
        if (exam.date) examText += ` taken on ${exam.date}`;
        return examText;
      })
      .filter(Boolean)
      .join(", ");
  };

  const parseRegions = (jsonString) => {
    if (!jsonString) return "None specified";

    try {
      // If it's already an object (not a string), use it directly
      const regions =
        typeof jsonString === "string" ? JSON.parse(jsonString) : jsonString;

      if (!Array.isArray(regions) || regions.length === 0)
        return "None specified";

      return regions
        .filter((region) => region.regionName !== "Select Region")
        .map((region) => {
          // Handle both array and object formats for subRegions
          let countryList = "";

          if (region.subRegions) {
            if (
              Array.isArray(region.subRegions) &&
              region.subRegions.length > 0
            ) {
              // Extract labels from objects if they exist
              const countries = region.subRegions
                .map((sr) => sr.label || sr)
                .filter(Boolean);
              if (countries.length > 0) {
                countryList = ` (${countries.join(", ")})`;
              }
            } else if (typeof region.subRegions === "object") {
              // Handle case when subRegions is an object
              const countries = Object.values(region.subRegions)
                .map((sr) => (typeof sr === "object" ? sr.label : sr))
                .filter(Boolean);

              if (countries.length > 0) {
                countryList = ` (${countries.join(", ")})`;
              }
            }
          }

          return `${region.regionName}${countryList}`;
        })
        .join(", ");
    } catch (error) {
      console.error("Error parsing regions:", error, jsonString);
      return "None specified";
    }
  };

  const parseInternship = (jsonString) => {
    if (!jsonString) return "None";

    try {
      const intern = JSON.parse(jsonString);
      if (!intern || !intern.companyName) return "None";

      let internText = `${intern.companyName} (${
        intern.domain || "Unlisted domain"
      })`;
      if (intern.duration) {
        internText += `, Duration: ${intern.duration}`;
      }
      return internText;
    } catch (error) {
      console.error("Error parsing internship data:", error);
      return "None";
    }
  };

  const generateRagPrompt = (userData, educationalData, educationType) => {
    // Common sections for all education levels
    let prompt = `Find opportunities that match the profile of a ${educationType} student.
  
  PERSONAL DETAILS:
  - Name: ${userData.firstname} ${userData.lastname}
  - Gender: ${getGenderLabel(userData.gender)}
  - Location: ${userData.city || ""}, ${userData.homestate || ""}, ${
      userData.country || ""
    }
  `;

    // Add education-level specific details
    switch (educationType) {
      case "Below8th":
        prompt += prepareBelowEighthPrompt(educationalData);
        break;
      case "9-10th":
        prompt += prepareNinthTenthPrompt(educationalData);
        break;
      case "11-12th":
        prompt += prepareElevenTwelfthPrompt(educationalData);
        break;
      case "Bachelors":
        prompt += prepareBachelorsPrompt(educationalData);
        break;
      case "Masters":
        prompt += prepareMastersPrompt(educationalData);
        break;
      case "PhD":
        prompt += preparePhDPrompt(educationalData);
        break;
      default:
        prompt += "No specific educational details available.";
    }

    // Common closing for all prompts
    prompt += `
  
  Please find ten different opportunities including scholarships, competitions, programs, internships, 
  and courses that would be suitable for this student. For each opportunity, include:
  - Title and organization
  - Application deadline
  - Eligibility criteria (as a single text paragraph describing all requirements)
  - Location/country
  - Detailed description
  - Type of opportunity
  - Application link (if available)
  
  Focus on opportunities that closely match this student's educational background, 
  interests, and location. Return the results in a structured format for easy parsing and integration.`;

    return prompt;
  };

  // Function to fetch opportunities from RAG API and save to MongoDB
  const fetchAndSaveOpportunities = async (
    userId,
    personalData,
    educationalData,
    educationType,
    token
  ) => {
    const prompt = generateRagPrompt(
      personalData,
      educationalData,
      educationType
    );
    console.log("PROMPT BEING SENT TO RAG API:", prompt);

    // Instead of calling the RAG API directly, call our Node.js backend
    try {
      const response = await axios.post(
        "/api/submit-education",
        {
          userID: userId,
          educationLevel: educationType,
          education_data: educationalData,
          personal_data: personalData,
          prompt: prompt,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      console.log("Submit Education Response:", response.data);

      // The backend now handles the RAG API call and will save the data to MongoDB
      // We don't need to do anything else here as it's queued as a background job
    } catch (error) {
      console.error("Error submitting education for opportunities:", error);
      // We'll still continue with registration even if this fails
    }
  };

  // Handle final form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsLoading(true);

    const submissionEducationalDetails = {
      ...educationalDetails,
      exams: educationalDetails.exams
        ? JSON.stringify(educationalDetails.exams)
        : "",
      futurefields: educationalDetails.futurefields
        ? JSON.stringify(educationalDetails.futurefields)
        : "",
      olympiadparticipated: educationalDetails.olympiadparticipated
        ? JSON.stringify(educationalDetails.olympiadparticipated)
        : "",
    };

    try {
      if (!access_token) {
        toast.error(
          "Authentication token is missing. Please restart registration or log in.",
          {
            position: "top-center",
          }
        );
        resetForm();
        navigate("/register");
        return;
      }

      const authHeaders = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access_token}`,
      };

      // Save user type
      await axios.post(
        "/api/saveUserType",
        { userID: generatedUserId, userType },
        { headers: authHeaders, withCredentials: true }
      );

      // Save personal details
      await axios.post(
        SAVE_PERSONALDETAILS_ROUTE,
        {
          firstname,
          lastname,
          country,
          city,
          homestate,
          pin,
          gender,
          userID: generatedUserId,
        },
        { headers: authHeaders, withCredentials: true }
      );

      // Save educational category
      if (educationLevel) {
        await axios.post(
          SAVE_CATEGORY,
          { userID: generatedUserId, category: educationLevel },
          { headers: authHeaders, withCredentials: true }
        );
      }

      // Save educational details
      let educationalEndpoint = "";
      switch (educationLevel) {
        case "Below8th":
          educationalEndpoint = "/api/below8thuser";
          break;
        case "9-10th":
          educationalEndpoint = "/api/ninetenth";
          break;
        case "11-12th":
          educationalEndpoint = "/api/eleven12thuser";
          break;
        case "Bachelors":
          educationalEndpoint = "/api/bachelors";
          break;
        case "Masters":
          educationalEndpoint = "/api/masters";
          break;
        case "PhD":
          educationalEndpoint = "/api/phd";
          break;
      }

      if (educationalEndpoint) {
        const educationalData = {
          ...submissionEducationalDetails,
          userID: generatedUserId,
        };
        await axios.post(educationalEndpoint, educationalData, {
          headers: authHeaders,
          withCredentials: true,
        });

        const personalData = { gender, country, city, firstname, lastname };
        fetchAndSaveOpportunities(
          generatedUserId,
          personalData,
          educationalDetails,
          educationLevel,
          access_token
        );
      }

      // Set login route to profile
      await axios.post(
        SAVE_LOGIN_ROUTE,
        { userID: generatedUserId, loginroute: "profile" },
        { headers: authHeaders, withCredentials: true }
      );

      // Save complete user data to localStorage for easy access in the NewProfile component
      const completeUserData = {
        // Personal information
        userID: generatedUserId,
        firstname,
        lastname,
        gender,
        country,
        city,
        homestate,
        pin,
        category: educationLevel,
        userType,

        // Authentication data
        auth: {
          email: localStorage.getItem("registrationState")
            ? JSON.parse(localStorage.getItem("registrationState")).email
            : "",
          phno: localStorage.getItem("registrationState")
            ? JSON.parse(localStorage.getItem("registrationState")).phno
            : "",
        },

        // Educational details - store with full value/label structure
        education: {
          ...educationalDetails,
          categoryType: educationLevel,
        },
      };

      // Store the complete data in localStorage
      localStorage.setItem("userData", JSON.stringify(completeUserData));

      toast.success("Registration completed successfully!", {
        position: "top-center",
      });
      setField("isCompleted", true);
      resetForm();
      navigate("/login");
    } catch (err) {
      console.error("Registration error:", err);
      const errorMessage =
        err.response?.data?.message || "Registration failed. Please try again.";
      toast.error(errorMessage, { position: "top-center" });

      if (err.response?.status === 401) {
        toast.error("Session expired. Please restart registration.", {
          position: "top-center",
        });
        resetForm();
        navigate("/register");
      } else if (err.response?.status === 500) {
        toast.error("Server error occurred. Please try again later.", {
          position: "top-center",
        });
      }
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  // Render appropriate form based on education level
  const renderEducationalForm = () => {
    switch (educationLevel) {
      case "Below8th":
        return (
          <Below8thForm
            onChange={handleEducationalDetailsChange}
            data={educationalDetails}
          />
        );
      case "9-10th":
        return (
          <NinthTenthForm
            onChange={handleEducationalDetailsChange}
            data={educationalDetails}
          />
        );
      case "11-12th":
        return (
          <ElevenTwelfthForm
            onChange={handleEducationalDetailsChange}
            data={educationalDetails}
          />
        );
      case "Bachelors":
        return (
          <BachelorsForm
            onChange={handleEducationalDetailsChange}
            data={educationalDetails}
          />
        );
      case "Masters":
        return (
          <MastersForm
            onChange={handleEducationalDetailsChange}
            data={educationalDetails}
          />
        );
      case "PhD":
        return (
          <PhdForm
            onChange={handleEducationalDetailsChange}
            data={educationalDetails}
          />
        );
      default:
        return <div>Please select an education level</div>;
    }
  };

  return (
    <div className="step-container">
      <div className="auth-header">
        <div className="auth-title">Educational Details</div>
        <div className="auth-logo">
          <img src={OpguruLogo} alt="OpGuru Logo" />
        </div>
      </div>

      <div className="welcome-message">
        Please provide details about your education
      </div>

      <form
        onSubmit={handleSubmit}
        className="registration-form educational-form"
      >
        {renderEducationalForm()}

        <div className="button-group">
          <button type="button" className="secondary-button" onClick={prevStep}>
            Back
          </button>

          <button type="submit" className="primary-button" disabled={loading}>
            {loading ? (
              <TailSpin
                visible={true}
                height="24"
                width="24"
                color="#ffffff"
                ariaLabel="tail-spin-loading"
                radius="1"
              />
            ) : (
              "Complete Registration"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EducationalDetailsStep;
